import type { EditorSDK, PageRef } from '@wix/platform-editor-sdk';

import { log, toMonitored } from '../../utils/monitoring';
import { APP_TOKEN } from '../constants';
import { registerAlwaysAvailableApps } from '../services/integration';
import * as pagesService from '../services/pages';
import * as routersService from '../services/routers';
import * as componentsWrapper from '../wrappers/components';
import * as menusWrapper from '../wrappers/menus';
import * as pagesWrapper from '../wrappers/pages';
import * as routersWrapper from '../wrappers/routers';
import { runAndWaitForApproval } from '../wrappers/transactions';

const refreshManifest = async (editorSDK: EditorSDK) => {
  const applicationPages = await editorSDK.document.pages.getApplicationPages(APP_TOKEN);
  if (applicationPages.length === 1) {
    await editorSDK.document.application.reloadManifest();
  }
};

const removeMembersAreaPage = async (options: {
  editorSDK: EditorSDK;
  id: string;
  shouldDisableTransaction?: boolean;
}) => {
  await toMonitored('removePage.platform', () => removeMembersAreaPagePlatform(options));
  await registerAlwaysAvailableApps(options.editorSDK);
  await refreshManifest(options.editorSDK);
};

const removeMembersAreaPageByPageId = async ({
  pageId,
  appDefinitionId,
  editorSDK,
  shouldDisableTransaction,
}: {
  pageId: string;
  appDefinitionId: string;
  editorSDK: EditorSDK;
  shouldDisableTransaction?: boolean;
}) => {
  const page = await pagesService.getPageByIntegrationApp({ editorSDK, app: { pageId, appDefinitionId } });
  return removeMembersAreaPage({
    editorSDK,
    id: page?.id!,
    shouldDisableTransaction,
  });
};

const getPageToNavigateAfterRemove = async (editorSDK: EditorSDK, { id }: { id: string }) => {
  const allPages = await pagesWrapper.getAllPages({ editorSDK });
  const currentPageRef = await pagesWrapper.getCurrentPage({ editorSDK });
  let navigateTo: PageRef | undefined = currentPageRef;
  const pageToDelete = allPages.find((p) => p.id === id);
  if (currentPageRef.id === pageToDelete?.id) {
    navigateTo = await pagesWrapper.getMainMembersPageRef({ editorSDK });
  }
  return navigateTo;
};

const removeMembersAreaPagePlatform = async ({
  editorSDK,
  id,
  shouldDisableTransaction,
}: {
  editorSDK: EditorSDK;
  id: string;
  shouldDisableTransaction?: boolean;
}) => {
  const componentRef = await componentsWrapper.getById({ editorSDK, id });
  if (!componentRef) {
    log('removeMembersAreaPagePlatform - componentRef not found');
    return;
  }
  const pageRef = await editorSDK.components.getPage(APP_TOKEN, { componentRef });
  if (!pageRef) {
    log('removeMembersAreaPagePlatform - pageRef not found');
    return;
  }

  const removePageTransaction = async () => {
    const routers = await routersWrapper.getAll(editorSDK);
    for (const router of Object.values(routers)) {
      const patternKey = routersWrapper.getPatternByPageRef(pageRef, router);
      if (patternKey) {
        const pattern = patternKey?.replace(/\/?(.*)/, '$1');
        await menusWrapper.removePatternFromAllMenus({ editorSDK, pattern });
        await routersService.deleteRouterPatternByPatternKey({ editorSDK, router, patternKey });
      }
    }
    await pagesWrapper.remove({
      editorSDK,
      pageRef,
      shouldShowEditorRemovePanel: false,
      pageToNavigateAfterRemove: await getPageToNavigateAfterRemove(editorSDK, { id }).catch((e) => {
        log('getPageToNavigateAfterRemove failed in removeMembersAreaPagePlatform', {
          extra: {
            error: e,
          },
        });
        return undefined;
      }),
    });
  };

  if (shouldDisableTransaction) {
    await removePageTransaction();
  } else {
    await runAndWaitForApproval(editorSDK, removePageTransaction);
  }
};

export { removeMembersAreaPage, removeMembersAreaPageByPageId };
